import { gtcDebounce } from 'gtcUtils';
import { getProductCardTemplate } from 'customAlgolia';

// const index = 'shopify_products';
// const collectionIndex = 'shopify_collections';
const productIndex = 'grafikr-com-products';
const collectionIndex = 'grafikr-com-collections';

let globalSheets = null;

function getGlobalStyleSheets() {
  if (!globalSheets) {
    const myOrigin = window.location.origin; // Get the current origin

    globalSheets = Array.from(document.styleSheets)
      .filter(sheet => {
        try {
          // Check if the stylesheet's href (if available) matches your origin
          return !sheet.href || new URL(sheet.href).origin === myOrigin;
        } catch (error) {
          // Handle invalid URLs gracefully (e.g., inline styles)
          return true; // Assume inline styles are from the same origin
        }
      })
      .map(sheet => {
        try {
          const newSheet = new CSSStyleSheet();
          newSheet.replaceSync(Array.from(sheet.cssRules).map(rule => rule.cssText).join(' '));
          return newSheet;
        } catch (error) {
          console.error("Error processing stylesheet:", error);
          return null; // Or handle the error differently
        }
      })
      .filter(sheet => sheet !== null); // Remove any failed sheets
  }

  return globalSheets;
}

function addGlobalStylesToShadowRoot(shadowRoot) {
  shadowRoot.adoptedStyleSheets = getGlobalStyleSheets();
}

function getSearchCollectionTemplate(collection, index) {
  if (collection.image) {
    const imageUrl = new URL(collection.image);
    imageUrl.searchParams.set('width', collection.image.width);
    const imageTag = `<img src="${ imageUrl.toString() }" sizes="(max-width: 699px) 160px, 500px" srcset="${ imageUrl.toString() } 200w, ${ imageUrl.toString() } 300w, ${ imageUrl.toString() } 400w, ${ imageUrl.toString() } 500w, ${ imageUrl.toString() } 600w, ${ imageUrl.toString() } 800w, ${ imageUrl.toString() } 1000w, ${ imageUrl.toString() } 1200w, ${ imageUrl.toString() } 1400w" alt="${ collection.title }">`;
    html = html.replace('{{ collection.image | image_url: width: collection.image.width | image_tag: sizes: \'(max-width: 699px) 160px, 500px\', widths: \'200,300,400,500,600,800,1000,1200,1400\' }}', imageTag);
  }

  const html = `
  <a 
  data-object-id="${ collection.objectID }"
  data-position="${ index }"
  data-index-name="${ collectionIndex }"
  href="/collections/${ collection.handle }" class="v-stack gap-3 sm:gap-5">
    ${ collection.image ? imageTag : '' }
    <span class="h6 sm:h5">${ collection.title }</span>
  </a>  
`;
  return html;
}

document.addEventListener('DOMContentLoaded', () => {
  class AlgoliaSearch extends HTMLElement {
    constructor() {
      super();
      this.settings = {}
      this.dom = {};

      this.onChange = gtcDebounce(this.onChange.bind(this), 244);
      this.submit = this.submit.bind(this);

      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="results"></slot>`));

      this.resultSlot = this.shadowRoot.querySelector('slot[name="results"]');

      addGlobalStylesToShadowRoot(this.shadowRoot);
    }

    connectedCallback() {
      this.dom = {
        form: document.querySelector(`form[aria-owns=${ this.id }]`),
        input: document.querySelector(`form[aria-owns=${ this.id }] input[type="search"]`),
        submitContainer: document.querySelector(`form[aria-owns=header-algolia-search]`).parentElement.querySelector('.algolia-search-form__submit-container')
      }

      this.addEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    addEventListeners() {
      this.dom.input.addEventListener('input', this.onChange);
    }

    removeEventListeners() {
      this.dom.input.removeEventListener('input', this.search);
    }

    getQuery() {
      return this.dom.input.value;
    }

    submit() {
      this.dom.form.submit();
    }

    async onChange() {
      await this.search(this.getQuery());

      const submitButton = this.shadowRoot.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.addEventListener('click', this.submit);
      }
    }

    async search(query) {
      if (!query) {
        return this.noResults();
      }

      const response = await searchClient.search([
        {
          indexName: productIndex,
          filters: 'available: true',
          query,
          distinct: 1,
          params: {
            hitsPerPage: 12,
          },
        },
        {
          indexName: collectionIndex,
          query,
          distinct: 1,
          params: {
            hitsPerPage: 10,
          },
        }
      ])
      .then(({ results }) => {

        return results.reduce((acc, result) => {
          if (result.index === productIndex) {
            // This should be products instead of hits
            acc.products = result;
            this.settings.products = result;
          }

          if (result.index === collectionIndex) {
            // This should be collections instead of hits
            acc.collections = result;
            this.settings.collections = result;
          }

          // This should return an object with products and collections
          return acc;
        }, {});
      });

      if (response.products.hits.length === 0) {
        return this.noResultsWithQuery();
      } else {

        aa('viewedObjectIDs', {
          "eventType": "view",
          "eventName": "Hits Viewed",
          "index": productIndex,
          queryID: response.products.queryID,
          objectIDs: [...response.products.hits.map(product => product.objectID)],
        });

        return this.results({ response });
      }
    }

    noResults() {
      this.resultSlot.innerHTML = '';
      this.dom.submitContainer.classList.add('hidden');
    }

    noResultsWithQuery() {
      this.resultSlot.innerHTML = `<p class="predictive-search__no-results text-lg">${ window.themeVariables.strings.algolia.noResults }</p>`;
      this.dom.submitContainer.classList.add('hidden');
    }

    async results({ response }) {
      this.resultSlot.innerHTML = '';

      const fragment = document.createDocumentFragment();

      fragment.appendChild(await this.resultTemplate({ response }));
      this.resultSlot.replaceChildren(fragment);
      this.dom.submitContainer.classList.remove('hidden');

    }

    render() {
      this.resultSlot.innerHTML = '';
    }

    resultTemplate = async ({ response }) => {
      
      const html = `
        <div class="predictive-search__results ${ response.collections.hits.length > 0 ? 'predictive-search__results--with-suggestions' : '' }">
          ${ response.collections.hits.length > 0
            ? `
              <div class="predictive-search__resource-item">
                <div class="v-stack gap-6 sm:gap-8">
                  <p class="predictive-search__category h6 text-subdued">
                    ${window.themeVariables.strings.algolia.collections}
                  </p>

                  <div class="predictive-search__suggestions scroll-area bleed md:unbleed">
                    ${response.collections ? response.collections.hits.map((collection, index) => `
                      <div class="predictive-search-result-item product-card" data-type="query">
                        ${getSearchCollectionTemplate(collection, index)}
                      </div>
                    `).join('') : ''}
                  </div>
                </div>
              </div>
            `
            : ''
          }

          <x-tabs class="predictive-search__tabs content-tabs">
            <template shadowrootmode="open">
              <style>
                .scrollable::-webkit-scrollbar {
                  display: none;
                }
              </style>

              <div part="tab-list-scrollable" class="scrollable">
                <slot role="tablist" part="tab-list" name="title"></slot>
              </div>

              <slot part="tab-panels" name="content"></slot>
            </template>

            ${response.products.hits.length > 0
              ? `
                <button type="button" class="h6" role="tab" slot="title">
                  ${window.themeVariables.strings.algolia.products}
                </button>

                <div class="predictive-search__resource-item" role="tabpanel" slot="content">
                  <div class="v-stack gap-8 sm:gap-12">
                    <div class="predictive-search__products" style="--product-list-items-per-row: ${ response.products.hits.length > 0 ? 4 : 5 }">
                      ${response.products.hits.map((product, index) => `
                        <div class="predictive-search-result-item product-card" data-type="product">
                          ${getProductCardTemplate({ product, queryID: response.products.queryID, quickAdd: true })}
                        </div>
                      `).join('')}
                    </div>
                  </div>
                </div>
            ` : '' }

            ${response.collections_if_active?.length > 0
              ? `
                <button type="button" class="h6" role="tab" slot="title">Collections</button>

                <div class="predictive-search__resource-item" role="tabpanel" slot="content">
                  <div class="v-stack gap-8 sm:gap-12">
                    <div class="predictive-search__collections" style="--collection-list-items-per-row: ${ response.products.hits.length > 0 ? 4 : 5 }">
                      ${response.collections.hits.slice(0, response.products.hits.length > 0 ? 4 : 5).map(collection => `
                        <div class="predictive-search-result-item product-card" data-type="collection">${ getSearchCollectionTemplate(collection) }</div>
                      `).join('')}
                    </div>
                  </div>
                </div>
              `
              : ''
            }
          </x-tabs>
        </div>
      `;


      return this.html(html);
    }

    html(strings, ...values) {
      const template = document.createElement('template');
      template.innerHTML = this.interpolate(strings, values);

      return template.content;
    }

    interpolate(strings, values) {
      if (!Array.isArray(strings)) strings = [strings];
      if (!Array.isArray(values)) values = [values];

      return strings.reduce((result, string, i) => {
        const value = values[i] ? values[i] : '';
        return result + string + value;
      }, '');
    }
  }

  if (!customElements.get('algolia-search')) {
    customElements.define('algolia-search', AlgoliaSearch);
  }
});
